// src/encounter/terrain/lib/terrain-catalog.ts
// The Terrain tab's reference catalog: the body systems, the markers each one reads, and the guideline bands with their
// citations. Pure data and pure functions: no React, no fetch.
//
// CLINICAL VALUES PENDING SAMER'S REVIEW. Copied verbatim from Samer's demo (scrips-demo-samer.vercel.app, view=terrain,
// bundle index-BiAqvkQZ.js, 4 Nov 2026), whose own catalog is labelled "Not clinically signed off yet" (generated
// 2026-09-30). Nothing clinical is written here that the demo does not carry; where this file goes beyond the demo it says so:
//  - SYSTEM membership is the demo's own state log (get_body_system_state) for heart-vessels, metabolism and
//    inflammation-immunity. The demo has no state for Omar on liver-kidney-blood, hormones or sleep-recovery-airway, so
//    eGFR, UACR, vitamin D, REI, Lp(a), non-HDL and total cholesterol are placed by the band's own system (marked INFERRED).
//  - LOINC lists come from backend-repos-00's mapping (the demo bundle names only 1884-6, 13457-7, 2571-8, 4548-4, 1558-6,
//    30522-7); every other code is marked NOT IN DEMO and needs the same review.
//  - `verification` is the demo's own row status ("verified" / "no reference on file").

export type TerrainSystemId =
  | 'heart-vessels'
  | 'metabolism'
  | 'inflammation-immunity'
  | 'liver-kidney-blood'
  | 'hormones'
  | 'sleep-recovery-airway'
  | 'brain-nervous';

export interface TerrainSystem {
  id: TerrainSystemId;
  /** The demo's system label, verbatim. */
  label: string;
  /** The demo's radar axis name (its short map), verbatim. */
  short: string;
}

export const TERRAIN_SYSTEMS: readonly TerrainSystem[] = [
  { id: 'heart-vessels', label: 'Heart & vessels', short: 'Heart' },
  { id: 'metabolism', label: 'Metabolism', short: 'Metabolism' },
  { id: 'inflammation-immunity', label: 'Inflammation & immunity', short: 'Immunity' },
  { id: 'liver-kidney-blood', label: 'Liver-kidney-blood', short: 'Liver & kidney' },
  { id: 'hormones', label: 'Hormones', short: 'Hormones' },
  { id: 'sleep-recovery-airway', label: 'Sleep-recovery-airway', short: 'Sleep & airway' },
  { id: 'brain-nervous', label: 'Brain & nervous system', short: 'Brain' },
];

export interface TerrainCitation {
  title: string;
  journal?: string;
  year: number;
  pmid?: string;
  doi?: string;
  /** The demo's own check of this citation: 'verified', or 'no reference on file'. */
  verification?: 'verified' | 'no reference on file';
}

export interface TerrainBand {
  /** The demo's band id, verbatim ('band:tg:< 1.7 mmol/L'). */
  id: string;
  /** 'threshold' where the demo's claim says threshold or cut-off ("not a target"); else 'target'. */
  kind: 'target' | 'threshold';
  /** In `unit`. `low` is inclusive; `high` is exclusive unless `highInclusive` (a printed range such as 18.5-24.9). */
  low?: number;
  high?: number;
  highInclusive?: boolean;
  /** Blood pressure only: the diastolic bound of '< 130/80' (exclusive); `high` is the systolic one. */
  diastolicHigh?: number;
  unit: string;
  /** The demo's claim, verbatim. */
  claim: string;
  /** The card line: the claim's bound plus the source's short name. */
  short: string;
  /** The demo's source, verbatim. */
  source: string;
  citation: TerrainCitation;
  /** ApoB / LDL-C / non-HDL: the band applies only to a patient whose ESC risk category is on record. */
  needsRiskCategory?: boolean;
}

export interface TerrainMarker {
  key: string;
  label: string;
  kind: 'lab' | 'vital';
  /** Labs: every accepted LOINC. */
  loincs: readonly string[];
  vital?: 'bp' | 'bmi' | 'weight';
  systems: readonly TerrainSystemId[];
  unit: string;
  /** [] = no guideline target named. */
  bands: readonly TerrainBand[];
}

// ── Citations (demo rows, verbatim) ───────────────────────────────────────────────────────────────────────────────────
const ESC_EAS_2019: TerrainCitation = {
  title:
    '2019 ESC/EAS Guidelines for the management of dyslipidaemias: lipid modification to reduce cardiovascular risk (Mach F et al.)',
  journal: 'European Heart Journal',
  year: 2019,
  pmid: '31504418',
  doi: '10.1093/eurheartj/ehz455',
};
const verified = (c: TerrainCitation): TerrainCitation => ({ ...c, verification: 'verified' });
const unverified = (c: TerrainCitation): TerrainCitation => ({ ...c, verification: 'no reference on file' });

const EAS_2022_LPA: TerrainCitation = {
  title:
    'Lipoprotein(a) in atherosclerotic cardiovascular disease and aortic stenosis: a European Atherosclerosis Society consensus statement (Kronenberg F et al.)',
  journal: 'European Heart Journal',
  year: 2022,
  pmid: '36036785',
  doi: '10.1093/eurheartj/ehac361',
};
const ACC_AHA_2017: TerrainCitation = {
  title:
    '2017 ACC/AHA/AAPA/ABC/ACPM/AGS/APhA/ASH/ASPC/NMA/PCNA Guideline for the Prevention, Detection, Evaluation, and Management of High Blood Pressure in Adults (Whelton PK et al.)',
  journal: 'Hypertension',
  year: 2017,
  pmid: '29133356',
  doi: '10.1161/HYP.0000000000000065',
};
const ADA_2025: TerrainCitation = {
  title: 'ADA Standards of Care in Diabetes-2025, Section 2: Diagnosis and Classification of Diabetes',
  journal: 'Diabetes Care',
  year: 2025,
  pmid: '39651986',
  doi: '10.2337/dc25-S002',
};
const WHO_BMI: TerrainCitation = {
  title:
    'WHO Technical Report Series 894: Obesity: preventing and managing the global epidemic (Table 2.1, Classification of adults according to BMI)',
  journal: 'World Health Organ Tech Rep Ser',
  year: 2000, // the demo prints 2e3
  pmid: '11234459',
};
const AHA_CDC_2003: TerrainCitation = {
  title:
    'Markers of inflammation and cardiovascular disease: application to clinical and public health practice: A statement for healthcare professionals from the Centers for Disease Control and Prevention and the American Heart Association (Pearson TA et al.)',
  journal: 'Circulation',
  year: 2003,
  pmid: '12551878',
  doi: '10.1161/01.cir.0000052939.59093.45',
};
const KDIGO_2012: TerrainCitation = {
  title: 'KDIGO 2012 Clinical Practice Guideline for the Evaluation and Management of CKD, Chapter 1: Definition and classification of CKD',
  journal: 'Kidney Int Suppl',
  year: 2013,
  pmid: '25018975',
  doi: '10.1038/kisup.2012.64',
};
const ENDO_2011: TerrainCitation = {
  title: 'Holick MF et al. Evaluation, treatment, and prevention of vitamin D deficiency: an Endocrine Society clinical practice guideline',
  journal: 'J Clin Endocrinol Metab',
  year: 2011,
  pmid: '21646368',
  doi: '10.1210/jc.2011-0385',
};
const AASM: TerrainCitation = {
  title:
    'Epstein LJ, Kristo D, Strollo PJ Jr, et al. Clinical guideline for the evaluation, management and long-term care of obstructive sleep apnea in adults (AASM Adult OSA Task Force). Substituted for the AASM Scoring Manual, which is not in PubMed. The AASM 1999 task force report (Sleep 22:667-89, PMID 10450601, doi 10.1093/sleep/22.5.667) also sets the bands but is closed access with no PubMed abstract, so it could not be quoted.',
  journal: 'J Clin Sleep Med',
  year: 2009,
  pmid: '19960649',
  doi: '10.5664/jcsm.27497',
};

// ── Bands (the demo's 15 rows: id, claim and source verbatim) ─────────────────────────────────────────────────────────
const B = {
  apobVeryHigh: {
    id: 'band:apob:< 0.65 g/L', kind: 'target', high: 0.65, unit: 'g/L',
    claim: 'target < 0.65 g/L, very-high risk', short: 'Target < 0.65 g/L · ESC/EAS 2019',
    source: 'ESC/EAS 2019 dyslipidaemia guideline', citation: verified(ESC_EAS_2019), needsRiskCategory: true,
  },
  apobHigh: {
    id: 'band:apob:< 0.80 g/L', kind: 'target', high: 0.8, unit: 'g/L',
    claim: 'target < 0.80 g/L, high risk', short: 'Target < 0.80 g/L · ESC/EAS 2019',
    source: 'ESC/EAS 2019 dyslipidaemia guideline', citation: verified(ESC_EAS_2019), needsRiskCategory: true,
  },
  ldl: {
    id: 'band:ldl:< 1.4 mmol/L', kind: 'target', high: 1.4, unit: 'mmol/L',
    claim: '< 1.4 mmol/L, very-high risk', short: 'Target < 1.4 mmol/L · ESC/EAS 2019',
    source: 'ESC/EAS 2019 dyslipidaemia guideline', citation: verified(ESC_EAS_2019), needsRiskCategory: true,
  },
  nonhdl: {
    id: 'band:nonhdl:< 2.6 mmol/L', kind: 'target', high: 2.6, unit: 'mmol/L',
    claim: '< 2.6 mmol/L, very-high risk', short: 'Target < 2.6 mmol/L · ESC/EAS 2019',
    source: 'ESC/EAS 2019 dyslipidaemia guideline', citation: unverified(ESC_EAS_2019), needsRiskCategory: true,
  },
  tg: {
    id: 'band:tg:< 1.7 mmol/L', kind: 'target', high: 1.7, unit: 'mmol/L',
    claim: 'target < 1.7 mmol/L, lower risk', short: 'Target < 1.7 mmol/L · ESC/EAS 2019',
    source: 'ESC/EAS 2019 dyslipidaemia guideline', citation: unverified(ESC_EAS_2019),
  },
  lpa: {
    id: 'band:lpa:< 75 nmol/L', kind: 'threshold', high: 75, unit: 'nmol/L',
    claim: 'risk threshold < 75 nmol/L (not a target)', short: 'Risk threshold < 75 nmol/L · EAS 2022',
    source: 'EAS 2022 Lp(a) consensus statement', citation: verified(EAS_2022_LPA),
  },
  bp: {
    id: 'band:bp:< 130/80 mmHg', kind: 'target', high: 130, diastolicHigh: 80, unit: 'mmHg',
    claim: 'target < 130/80 mmHg', short: 'Target < 130/80 mmHg · ACC/AHA 2017',
    source: 'ACC/AHA 2017 blood pressure guideline', citation: unverified(ACC_AHA_2017),
  },
  bmi: {
    id: 'band:bmi:18.5-24.9 kg/m²', kind: 'target', low: 18.5, high: 24.9, highInclusive: true, unit: 'kg/m²',
    claim: '18.5-24.9 kg/m²', short: '18.5-24.9 kg/m² · WHO',
    source: 'WHO adult BMI classification', citation: verified(WHO_BMI),
  },
  glucoseFasting: {
    id: 'band:glucose_fasting:< 5.6 mmol/L', kind: 'target', high: 5.6, unit: 'mmol/L',
    claim: '< 5.6 mmol/L, normal fasting glucose', short: '< 5.6 mmol/L · ADA 2025',
    source: 'ADA Standards of Care in Diabetes 2025', citation: verified(ADA_2025),
  },
  hba1c: {
    id: 'band:hba1c:5.7 %', kind: 'threshold', high: 5.7, unit: '%',
    claim: 'prediabetes threshold 5.7 % (range 5.7-6.4 %)', short: 'Prediabetes threshold 5.7 % · ADA 2025',
    source: 'ADA Standards of Care in Diabetes 2025', citation: verified(ADA_2025),
  },
  hscrp: {
    id: 'band:hscrp:< 1.0 mg/L', kind: 'threshold', high: 1.0, unit: 'mg/L',
    claim: 'low-risk cut-off < 1.0 mg/L, lower cardiovascular-risk category', short: 'Low-risk cut-off < 1.0 mg/L · AHA/CDC 2003',
    source: 'AHA/CDC 2003 scientific statement on inflammatory markers', citation: unverified(AHA_CDC_2003),
  },
  egfr: {
    id: 'band:egfr:≥ 90 mL/min/1.73m²', kind: 'threshold', low: 90, unit: 'mL/min/1.73m²',
    claim: '≥ 90 mL/min/1.73m², category G1 threshold', short: '≥ 90 mL/min/1.73m² · KDIGO G1',
    source: 'KDIGO 2012 CKD guideline (G1)', citation: verified(KDIGO_2012),
  },
  uacr: {
    id: 'band:uacr:< 3 mg/mmol', kind: 'threshold', high: 3, unit: 'mg/mmol',
    claim: '< 3 mg/mmol, category A1 threshold', short: '< 3 mg/mmol · KDIGO A1',
    source: 'KDIGO 2012 CKD guideline (A1)', citation: verified(KDIGO_2012),
  },
  vitd: {
    id: 'band:vitd:≥ 75 nmol/L', kind: 'target', low: 75, unit: 'nmol/L',
    claim: '≥ 75 nmol/L, sufficiency', short: '≥ 75 nmol/L · Endocrine Society 2011',
    source: 'Endocrine Society 2011 vitamin D guideline', citation: unverified(ENDO_2011),
  },
  rei: {
    id: 'band:rei:< 5 events/h', kind: 'target', high: 5, unit: 'events/h',
    claim: '< 5 events/h', short: '< 5 events/h · AASM',
    source: 'AASM scoring criteria; report classification mild', citation: verified(AASM),
  },
} as const satisfies Record<string, TerrainBand>;

// ── Markers ───────────────────────────────────────────────────────────────────────────────────────────────────────────
export const TERRAIN_MARKERS: readonly TerrainMarker[] = [
  // Systems from the demo's state log (heart-vessels: apob, ldl-c, triglycerides, hdl, hscrp, blood-pressure).
  { key: 'apob', label: 'ApoB', kind: 'lab', loincs: ['1884-6'], systems: ['heart-vessels'], unit: 'g/L', bands: [B.apobVeryHigh, B.apobHigh] },
  // LOINC 18262-6 NOT IN DEMO (backend-repos-00's mapping).
  { key: 'ldl', label: 'LDL-C', kind: 'lab', loincs: ['13457-7', '18262-6'], systems: ['heart-vessels'], unit: 'mmol/L', bands: [B.ldl] },
  // LOINC NOT IN DEMO. System INFERRED (the band is a lipid band).
  { key: 'nonhdl', label: 'Non-HDL-C', kind: 'lab', loincs: ['43396-1'], systems: ['heart-vessels'], unit: 'mmol/L', bands: [B.nonhdl] },
  // LOINC NOT IN DEMO. System INFERRED (cardiovascular risk threshold).
  { key: 'lpa', label: 'Lp(a)', kind: 'lab', loincs: ['43583-4'], systems: ['heart-vessels'], unit: 'nmol/L', bands: [B.lpa] },
  // LOINC NOT IN DEMO. System INFERRED. No band in the demo.
  { key: 'totalchol', label: 'Total cholesterol', kind: 'lab', loincs: ['2093-3'], systems: ['heart-vessels'], unit: 'mmol/L', bands: [] },
  // Demo log: in heart-vessels AND metabolism. LOINC 2085-9 NOT IN DEMO. No band in the demo.
  { key: 'hdl', label: 'HDL-C', kind: 'lab', loincs: ['2085-9'], systems: ['heart-vessels', 'metabolism'], unit: 'mmol/L', bands: [] },
  // Demo log: in heart-vessels AND metabolism.
  { key: 'tg', label: 'Triglycerides', kind: 'lab', loincs: ['2571-8'], systems: ['heart-vessels', 'metabolism'], unit: 'mmol/L', bands: [B.tg] },
  // Demo log: in heart-vessels AND inflammation-immunity.
  { key: 'hscrp', label: 'hsCRP', kind: 'lab', loincs: ['30522-7'], systems: ['heart-vessels', 'inflammation-immunity'], unit: 'mg/L', bands: [B.hscrp] },
  { key: 'bp', label: 'Blood pressure', kind: 'vital', loincs: [], vital: 'bp', systems: ['heart-vessels'], unit: 'mmHg', bands: [B.bp] },
  // Demo log (metabolism): hba1c, fasting-glucose, bmi, weight.
  { key: 'hba1c', label: 'HbA1c', kind: 'lab', loincs: ['4548-4'], systems: ['metabolism'], unit: '%', bands: [B.hba1c] },
  { key: 'glucose_fasting', label: 'Fasting glucose', kind: 'lab', loincs: ['1558-6'], systems: ['metabolism'], unit: 'mmol/L', bands: [B.glucoseFasting] },
  { key: 'bmi', label: 'BMI', kind: 'vital', loincs: [], vital: 'bmi', systems: ['metabolism'], unit: 'kg/m²', bands: [B.bmi] },
  { key: 'weight', label: 'Weight', kind: 'vital', loincs: [], vital: 'weight', systems: ['metabolism'], unit: 'kg', bands: [] },
  // LOINCs NOT IN DEMO. System INFERRED (KDIGO kidney categories).
  { key: 'egfr', label: 'eGFR', kind: 'lab', loincs: ['98979-8', '62238-1', '33914-3'], systems: ['liver-kidney-blood'], unit: 'mL/min/1.73m²', bands: [B.egfr] },
  { key: 'uacr', label: 'UACR', kind: 'lab', loincs: ['9318-7', '14959-1'], systems: ['liver-kidney-blood'], unit: 'mg/mmol', bands: [B.uacr] },
  // LOINC NOT IN DEMO. System INFERRED (the demo's radar counts Hormones among systems with banded markers).
  { key: 'vitd', label: 'Vitamin D (25-OH)', kind: 'lab', loincs: ['1989-3', '62292-8', '68438-1'], systems: ['hormones'], unit: 'nmol/L', bands: [B.vitd] },
  // No LOINC (a sleep-study report value). System INFERRED.
  { key: 'rei', label: 'REI', kind: 'lab', loincs: [], systems: ['sleep-recovery-airway'], unit: 'events/h', bands: [B.rei] },
];

const BY_LOINC: ReadonlyMap<string, TerrainMarker> = new Map(TERRAIN_MARKERS.flatMap((m) => m.loincs.map((l) => [l, m] as const)));

export function markersForSystem(id: TerrainSystemId): TerrainMarker[] {
  return TERRAIN_MARKERS.filter((m) => m.systems.includes(id));
}

export function markerForLoinc(loinc: string): TerrainMarker | undefined {
  return BY_LOINC.get(loinc.trim());
}

/** Where `value` (in the band's unit) sits against the band. For blood pressure, `value` is the systolic reading. */
export function bandStatus(value: number, band: TerrainBand): 'within' | 'above' | 'below' {
  if (band.low !== undefined && value < band.low) return 'below';
  if (band.high !== undefined && (band.highInclusive ? value > band.high : value >= band.high)) return 'above';
  return 'within';
}

/** Blood pressure against '< 130/80': above when EITHER the systolic or the diastolic reaches its bound. */
export function bpBandStatus(systolic: number, diastolic: number, band: TerrainBand): 'within' | 'above' {
  const sys = band.high !== undefined && systolic >= band.high;
  const dia = band.diastolicHigh !== undefined && diastolic >= band.diastolicHigh;
  return sys || dia ? 'above' : 'within';
}
